import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import Chart from 'chart.js/auto';
import { forkJoin } from 'rxjs';
import { AncDataService } from 'src/app/services/ANC/anc-data.service';
import { DeviceService } from 'src/app/services/devices/device.service';
import {
  SuntimesLegacyDayRow,
  SuntimesService,
} from 'src/app/services/sunTimes/sunTimesLegacy.service';

type SunTimesViewMode = 'chart' | 'grid';

interface GridHoverInfo {
  day: SuntimesLegacyDayRow;
  isNight: boolean;
  x: number;
  y: number;
}

interface SeasonTransitionMarker {
  dayIndex: number;
  dateLabel: string;
  dateStr: string;
  timeStr: string;
  conceptLabel: string;
  note: string;
}

@Component({
    selector: 'app-anc-suntimes-yearly',
    imports: [CommonModule, FormsModule, TranslateModule],
    templateUrl: './anc-suntimes-yearly.component.html',
    styleUrl: './anc-suntimes-yearly.component.scss'
})
export class AncSuntimesYearlyComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** Mismo rango que el reloj: año 17–50 → 2017–2050 (2000 + offset). */
  private static readonly MIN_CLOCK_YEAR = 2026;
  private static readonly MAX_CLOCK_YEAR = 2050;

  @Input() serial = '';

  @ViewChild('yearlyChart') chartCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('nightGridCanvas') nightGridCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('nightGridContainer') nightGridContainer!: ElementRef<HTMLElement>;

  loading = true;
  error = '';
  locationLabel = '';
  seasonChangeEnabled: boolean | null = null;
  seasonsInfo: {
    enable: number;
    winter: {
      startDay: number;
      startMonth: number;
      startHour: number;
      startMin: number;
    } | null;
    summer: {
      startDay: number;
      startMonth: number;
      startHour: number;
      startMin: number;
    } | null;
  } | null = null;
  selectedYear = Math.min(
    AncSuntimesYearlyComponent.MAX_CLOCK_YEAR,
    Math.max(AncSuntimesYearlyComponent.MIN_CLOCK_YEAR, new Date().getFullYear())
  );
  yearOptions: number[] = [];
  yearData: SuntimesLegacyDayRow[] = [];
  seasonTransitionMarkers: SeasonTransitionMarker[] = [];
  viewMode: SunTimesViewMode = 'chart';
  gridHover: GridHoverInfo | null = null;

  readonly nightFillColor = 'rgba(210, 198, 235, 0.32)';
  readonly dayFillColor = '#ffffff';
  readonly sunriseLineColor = 'rgba(247, 210, 26, 0.95)';
  readonly sunsetLineColor = 'rgba(108, 117, 125, 0.95)';
  readonly seasonTransitionLineColor = 'rgba(46, 142, 170, 0.9)';

  private chart: Chart | null = null;
  private viewReady = false;
  private pendingLoad = false;

  private readonly minutesPerDay = 1440;
  private readonly gridPadding = { top: 8, right: 12, bottom: 24, left: 44 };
  private gridPlot = { x: 0, y: 0, width: 0, height: 0, dayHeight: 0, minuteWidth: 0 };

  constructor(
    private _device: DeviceService,
    private _ancDataService: AncDataService,
    private _suntimesService: SuntimesService,
    private translate: TranslateService,
  ) {
    const { MIN_CLOCK_YEAR, MAX_CLOCK_YEAR } = AncSuntimesYearlyComponent;
    this.yearOptions = Array.from(
      { length: MAX_CLOCK_YEAR - MIN_CLOCK_YEAR + 1 },
      (_, index) => MIN_CLOCK_YEAR + index
    );
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.pendingLoad || this.serial) {
      this.loadData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['serial'] && this.serial) {
      if (this.viewReady) {
        this.loadData();
      } else {
        this.pendingLoad = true;
      }
    }
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
    this.chart = null;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.viewMode === 'grid' && this.yearData.length) {
      setTimeout(() => this.buildNightGrid(), 50);
    }
  }

  onYearChange(): void {
    this.gridHover = null;
    this.loadData();
  }

  setViewMode(mode: SunTimesViewMode): void {
    this.viewMode = mode;
    this.gridHover = null;

    if (mode === 'chart') {
      setTimeout(() => this.buildChart(), 0);
      return;
    }

    this.chart?.destroy();
    this.chart = null;
    setTimeout(() => this.buildNightGrid(), 0);
  }

  onGridMouseMove(event: MouseEvent): void {
    if (!this.nightGridCanvas?.nativeElement || !this.yearData.length) {
      return;
    }

    const canvas = this.nightGridCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const canvasX = (event.clientX - rect.left) * scaleX;
    const canvasY = (event.clientY - rect.top) * scaleY;

    const plotX = canvasX - this.gridPadding.left;
    const plotY = canvasY - this.gridPadding.top;

    if (
      plotX < 0 ||
      plotY < 0 ||
      plotX > this.gridPlot.width ||
      plotY > this.gridPlot.height
    ) {
      this.gridHover = null;
      return;
    }

    const dayIndex = Math.min(
      this.yearData.length - 1,
      Math.max(0, Math.floor(plotY / this.gridPlot.dayHeight))
    );
    const day = this.yearData[dayIndex];
    const minute = Math.min(
      this.minutesPerDay - 1,
      Math.max(0, Math.floor(plotX / this.gridPlot.minuteWidth))
    );

    this.gridHover = {
      day,
      isNight: this.isNightMinute(day, minute),
      x: event.clientX - rect.left + 12,
      y: event.clientY - rect.top - 8,
    };
  }

  onGridMouseLeave(): void {
    this.gridHover = null;
  }

  loadData(): void {
    if (!this.serial) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.seasonChangeEnabled = null;
    this.seasonsInfo = null;
    this.seasonTransitionMarkers = [];

    forkJoin({
      deviceInfo: this._device.getDeviceInfo(this.serial),
      deviceCfg: this._device.getModuleInfo(this.serial, 'device'),
      calendarCfg: this._device.getModuleInfo(this.serial, 'calendar'),
      seasons: this._device.getModuleInfo(this.serial, 'seasons'),
    }).subscribe({
      next: ({ deviceInfo, deviceCfg, calendarCfg, seasons }) => {
        const version = deviceInfo.hardware_version.split('.').pop() ?? '';
        const coords = this._ancDataService.getLocation(
          version,
          deviceCfg.cfg.country,
          deviceCfg.cfg.city
        );
        const ubicacion = this._ancDataService.getCountryCityNames(
          version,
          deviceCfg.cfg.country,
          deviceCfg.cfg.city
        );

        this.locationLabel = [ubicacion?.city, ubicacion?.country].filter(Boolean).join(', ');

        const seasonsInfo = this._ancDataService.formatSeasonData(seasons.cfg);
        this.seasonsInfo = seasonsInfo;
        this.seasonChangeEnabled = seasonsInfo.enable === 1;

        const corrSunrise = calendarCfg.cfg?.corrSunrise ?? 0;
        const corrSunset = calendarCfg.cfg?.corrSunset ?? 0;

        this.yearData = this._suntimesService.calculateSuntimesLegacyForYearWithGmtResolver(
          this.selectedYear,
          (date) => this._ancDataService.getGmtMinutesForDate(date, coords, seasonsInfo),
          coords?.latitude ?? 0,
          coords?.longitude ?? 0,
          'es-ES',
          corrSunrise,
          corrSunset
        );

        this.seasonTransitionMarkers = this.buildSeasonTransitionMarkers();

        this.loading = false;

        if (this.viewMode === 'chart') {
          setTimeout(() => this.buildChart(), 0);
        } else {
          setTimeout(() => this.buildNightGrid(), 0);
        }
      },
      error: () => {
        this.loading = false;
        this.error = 'sunTimesYearlyLoadError';
      },
    });
  }

  downloadCsv(): void {
    if (!this.yearData.length) {
      return;
    }

    const headers = [
      this.t('date'),
      this.t('weekday'),
      this.t('sunrise'),
      this.t('sunset'),
      `${this.t('dayLength')} (min)`,
      this.t('sunTimesCsvAllDay'),
      this.t('sunTimesCsvAllNight'),
    ];
    const rows: string[] = [];

    if (this.locationLabel) {
      rows.push(`${this.t('location')},${this.escapeCsvField(this.locationLabel)}`);
    }
    rows.push(`${this.t('sunTimesCsvYearLabel')},${this.selectedYear}`);
    rows.push(`${this.t('sunTimesCsvClockSeasonChange')},${this.seasonChangeEnabled ? this.t('active') : this.t('disabled')}`);

    const transitions = this.getSeasonTransitionsForYear(this.selectedYear);
    if (transitions.length) {
      rows.push('');
      rows.push(`${this.t('sunTimesCsvConcept')},${this.t('date')},${this.t('time')},${this.t('chargerDescription')}`);
      for (const transition of transitions) {
        rows.push(
          `${transition.label},${transition.dateStr},${transition.timeStr},${transition.note}`
        );
      }
    }

    rows.push('');
    rows.push(headers.join(','));

    const markersByDayIndex = new Map(
      this.seasonTransitionMarkers.map((marker) => [marker.dayIndex, marker])
    );

    for (let i = 0; i < this.yearData.length; i++) {
      const marker = markersByDayIndex.get(i);
      if (marker) {
        rows.push(this.buildSeasonChangeCsvRow(marker));
      }

      const day = this.yearData[i];
      const date = this.formatDate(day.date);
      const dayLength =
        day.sunriseMin != null && day.sunsetMin != null ? day.sunsetMin - day.sunriseMin : '';
      rows.push(
        [
          date,
          day.weekday,
          day.sunriseStr,
          day.sunsetStr,
          dayLength,
          day.isAllDay ? '1' : '0',
          day.isAllNight ? '1' : '0',
        ].join(',')
      );
    }

    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${this.serial}_orto_ocaso_${this.selectedYear}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  formatDate(date: Date): string {
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  private escapeCsvField(value: string): string {
    if (/[",\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  private buildSeasonChangeCsvRow(marker: SeasonTransitionMarker): string {
    return [
      `» ${this.t('sunTimesSeasonChangeMarker')}`,
      marker.conceptLabel,
      marker.dateStr,
      marker.timeStr,
      marker.note,
      '',
      '',
    ]
      .map((field) => this.escapeCsvField(field))
      .join(',');
  }

  private getSeasonTransitionsForYear(year: number): Array<{
    label: string;
    dateStr: string;
    timeStr: string;
    note: string;
    sortKey: number;
  }> {
    if (!this.seasonChangeEnabled || !this.seasonsInfo) {
      return [];
    }

    const transitions: Array<{
      label: string;
      dateStr: string;
      timeStr: string;
      note: string;
      sortKey: number;
    }> = [];

    const pushTransition = (
      season: {
        startDay: number;
        startMonth: number;
        startHour: number;
        startMin: number;
      } | null,
      label: string,
      note: string
    ): void => {
      if (!season) {
        return;
      }

      const { startDay, startMonth, startHour, startMin } = season;
      if (
        !Number.isFinite(startDay) ||
        !Number.isFinite(startMonth) ||
        !Number.isFinite(startHour) ||
        !Number.isFinite(startMin)
      ) {
        return;
      }

      transitions.push({
        label,
        dateStr: `${String(startDay).padStart(2, '0')}/${String(startMonth).padStart(2, '0')}/${year}`,
        timeStr: `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`,
        note,
        sortKey: startMonth * 100 + startDay,
      });
    };

    pushTransition(this.seasonsInfo.summer, this.t('sunTimesCsvSummerStart'), this.t('sunTimesCsvSummerNote'));
    pushTransition(this.seasonsInfo.winter, this.t('sunTimesCsvWinterStart'), this.t('sunTimesCsvWinterNote'));

    return transitions.sort((a, b) => a.sortKey - b.sortKey);
  }

  private buildSeasonTransitionMarkers(): SeasonTransitionMarker[] {
    const transitions = this.getSeasonTransitionsForYear(this.selectedYear);
    const markers: SeasonTransitionMarker[] = [];

    for (const transition of transitions) {
      const [day, month] = transition.dateStr.split('/').map(Number);
      const dayIndex = this.yearData.findIndex(
        (row) => row.date.getDate() === day && row.date.getMonth() + 1 === month
      );

      if (dayIndex <= 0) {
        continue;
      }

      markers.push({
        dayIndex,
        dateLabel: `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`,
        dateStr: transition.dateStr,
        timeStr: transition.timeStr,
        conceptLabel: transition.label,
        note: transition.note,
      });
    }

    return markers;
  }

  private buildChart(): void {
    if (this.viewMode !== 'chart' || !this.chartCanvas?.nativeElement || !this.yearData.length) {
      return;
    }

    this.chart?.destroy();

    const labels = this.yearData.map((day) => {
      const date = day.date;
      return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
    });
    const seasonMarkers = [...this.seasonTransitionMarkers];

    this.chart = new Chart(this.chartCanvas.nativeElement, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: this.t('sunrise'),
            data: this.yearData.map((day) => day.sunriseMin),
            borderColor: '#e3e330',
            backgroundColor: 'rgba(227, 227, 48, 0.12)',
            pointRadius: 0,
            borderWidth: 2,
            tension: 0.35,
            spanGaps: false,
          },
          {
            label: this.t('sunset'),
            data: this.yearData.map((day) => day.sunsetMin),
            borderColor: '#011e41',
            backgroundColor: 'rgba(1, 30, 65, 0.08)',
            pointRadius: 0,
            borderWidth: 2,
            tension: 0.35,
            spanGaps: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              title: (items) => {
                const index = items[0]?.dataIndex ?? 0;
                const day = this.yearData[index];
                if (!day) {
                  return '';
                }
                return `${this.formatDate(day.date)} (${day.weekday})`;
              },
              label: (context) => {
                const value = context.parsed.y;
                if (value == null || Number.isNaN(value)) {
                  return `${context.dataset.label}: --:--`;
                }
                return `${context.dataset.label}: ${this.minutesToHHMM(value)}`;
              },
              afterBody: (items) => {
                const index = items[0]?.dataIndex ?? 0;
                const day = this.yearData[index];
                if (!day || day.sunriseMin == null || day.sunsetMin == null) {
                  return [];
                }
                const duration = day.sunsetMin - day.sunriseMin;
                return [`${this.t('duration')}: ${Math.floor(duration / 60)}h ${duration % 60}m`];
              },
            },
          },
        },
        scales: {
          x: {
            ticks: {
              maxTicksLimit: 12,
            },
          },
          y: {
            min: 0,
            max: 1440,
            ticks: {
              stepSize: 120,
              callback: (value) => this.minutesToHHMM(Number(value)),
            },
          },
        },
      },
      plugins: [
        {
          id: 'dayNightBackground',
          beforeDatasetsDraw: (chart) => {
            const { ctx, chartArea, scales } = chart;
            const xScale = scales.x;
            const yScale = scales.y;
            if (!ctx || !chartArea || !xScale || !yScale) {
              return;
            }

            const dayCount = this.yearData.length;
            ctx.save();
            ctx.fillStyle = this.dayFillColor;
            ctx.fillRect(
              chartArea.left,
              chartArea.top,
              chartArea.right - chartArea.left,
              chartArea.bottom - chartArea.top
            );

            ctx.fillStyle = this.nightFillColor;

            for (let i = 0; i < dayCount; i++) {
              const day = this.yearData[i];
              const xCenter = xScale.getPixelForValue(i);
              const xPrev = i > 0 ? (xScale.getPixelForValue(i - 1) + xCenter) / 2 : chartArea.left;
              const xNext =
                i < dayCount - 1 ? (xCenter + xScale.getPixelForValue(i + 1)) / 2 : chartArea.right;
              const xLeft = i === 0 ? chartArea.left : xPrev;
              const xRight = i === dayCount - 1 ? chartArea.right : xNext;
              const width = xRight - xLeft;

              if (day.isAllNight) {
                ctx.fillRect(xLeft, chartArea.top, width, chartArea.bottom - chartArea.top);
                continue;
              }

              if (day.isAllDay) {
                continue;
              }

              const sunrise = day.sunriseMin;
              const sunset = day.sunsetMin;

              if (sunrise == null || sunset == null) {
                ctx.fillRect(xLeft, chartArea.top, width, chartArea.bottom - chartArea.top);
                continue;
              }

              const yBottom = yScale.getPixelForValue(0);
              const ySunrise = yScale.getPixelForValue(sunrise);
              const ySunset = yScale.getPixelForValue(sunset);

              if (sunrise > 0) {
                ctx.fillRect(xLeft, ySunrise, width, yBottom - ySunrise);
              }

              if (sunset < this.minutesPerDay) {
                ctx.fillRect(xLeft, chartArea.top, width, ySunset - chartArea.top);
              }
            }

            ctx.restore();
          },
        },
        {
          id: 'seasonTransitionMarkers',
          afterDatasetsDraw: (chart) => {
            if (!seasonMarkers.length) {
              return;
            }

            const { ctx, chartArea, scales } = chart;
            const xScale = scales.x;
            if (!ctx || !chartArea || !xScale) {
              return;
            }

            ctx.save();
            ctx.strokeStyle = this.seasonTransitionLineColor;
            ctx.fillStyle = '#2e8eaa';
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 4]);
            ctx.font = '11px sans-serif';

            for (const marker of seasonMarkers) {
              const xPrev = xScale.getPixelForValue(marker.dayIndex - 1);
              const xCurr = xScale.getPixelForValue(marker.dayIndex);
              const x = (xPrev + xCurr) / 2;

              ctx.beginPath();
              ctx.moveTo(x, chartArea.top);
              ctx.lineTo(x, chartArea.bottom);
              ctx.stroke();

              ctx.setLineDash([]);
              ctx.textAlign = 'left';
              ctx.textBaseline = 'bottom';
              ctx.fillText(marker.dateLabel, x + 4, chartArea.top + 14);
              ctx.setLineDash([6, 4]);
            }

            ctx.restore();
          },
        },
      ],
    });
  }

  private buildNightGrid(): void {
    if (this.viewMode !== 'grid' || !this.nightGridCanvas?.nativeElement || !this.yearData.length) {
      return;
    }

    const canvas = this.nightGridCanvas.nativeElement;
    const container = this.nightGridContainer?.nativeElement;
    const cssWidth = container?.clientWidth ?? 0;
    const cssHeight = container?.clientHeight ?? 0;

    if (!cssWidth || !cssHeight) {
      window.setTimeout(() => this.buildNightGrid(), 50);
      return;
    }

    const dayCount = this.yearData.length;
    const plotWidth = Math.max(200, cssWidth - this.gridPadding.left - this.gridPadding.right);
    const plotHeight = Math.max(200, cssHeight - this.gridPadding.top - this.gridPadding.bottom);
    const dpr = window.devicePixelRatio || 1;

    canvas.width = Math.floor(cssWidth * dpr);
    canvas.height = Math.floor(cssHeight * dpr);
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

    const plotX = this.gridPadding.left;
    const plotY = this.gridPadding.top;
    const dayHeight = plotHeight / dayCount;
    const minuteWidth = plotWidth / this.minutesPerDay;

    this.gridPlot = { x: plotX, y: plotY, width: plotWidth, height: plotHeight, dayHeight, minuteWidth };

    ctx.fillStyle = this.dayFillColor;
    ctx.fillRect(plotX, plotY, plotWidth, plotHeight);

    ctx.fillStyle = this.nightFillColor;

    for (let i = 0; i < dayCount; i++) {
      const day = this.yearData[i];
      const rowY = plotY + i * dayHeight;

      if (day.isAllNight) {
        ctx.fillRect(plotX, rowY, plotWidth, dayHeight + 0.5);
        continue;
      }

      if (day.isAllDay) {
        continue;
      }

      const sunrise = day.sunriseMin;
      const sunset = day.sunsetMin;

      if (sunrise == null || sunset == null) {
        ctx.fillRect(plotX, rowY, plotWidth, dayHeight + 0.5);
        continue;
      }

      if (sunrise > 0) {
        ctx.fillRect(plotX, rowY, sunrise * minuteWidth, dayHeight + 0.5);
      }

      if (sunset < this.minutesPerDay) {
        ctx.fillRect(plotX + sunset * minuteWidth, rowY, (this.minutesPerDay - sunset) * minuteWidth, dayHeight + 0.5);
      }
    }

    this.drawSunriseSunsetCurves(ctx, plotX, plotY, plotWidth, plotHeight, dayHeight, minuteWidth);
    this.drawGridAxes(ctx, plotX, plotY, plotWidth, plotHeight, dayCount, dayHeight);
    this.drawSeasonTransitionLines(ctx, plotX, plotY, plotWidth, plotHeight, dayHeight);
  }

  private drawSeasonTransitionLines(
    ctx: CanvasRenderingContext2D,
    plotX: number,
    plotY: number,
    plotWidth: number,
    plotHeight: number,
    dayHeight: number
  ): void {
    if (!this.seasonTransitionMarkers.length) {
      return;
    }

    ctx.save();
    ctx.strokeStyle = this.seasonTransitionLineColor;
    ctx.fillStyle = '#2e8eaa';
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.font = '11px sans-serif';

    for (const marker of this.seasonTransitionMarkers) {
      const y = plotY + marker.dayIndex * dayHeight;

      ctx.beginPath();
      ctx.moveTo(plotX, y);
      ctx.lineTo(plotX + plotWidth, y);
      ctx.stroke();

      ctx.setLineDash([]);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(marker.dateLabel, plotX + 4, y - 2);
      ctx.setLineDash([6, 4]);
    }

    ctx.restore();
  }

  private drawSunriseSunsetCurves(
    ctx: CanvasRenderingContext2D,
    plotX: number,
    plotY: number,
    plotWidth: number,
    plotHeight: number,
    dayHeight: number,
    minuteWidth: number
  ): void {
    const boundaryWidth = Math.max(2, Math.ceil(dayHeight));

    // Marcadores por día en el límite noche/día (más visibles con filas comprimidas)
    for (let i = 0; i < this.yearData.length; i++) {
      const day = this.yearData[i];
      if (day.isAllDay || day.isAllNight) {
        continue;
      }

      const rowY = plotY + i * dayHeight;
      const rowH = dayHeight + 0.5;

      if (day.sunriseMin != null) {
        const x = plotX + day.sunriseMin * minuteWidth;
        ctx.fillStyle = this.sunriseLineColor;
        ctx.fillRect(x - boundaryWidth / 2, rowY, boundaryWidth, rowH);
      }

      if (day.sunsetMin != null) {
        const x = plotX + day.sunsetMin * minuteWidth;
        ctx.fillStyle = this.sunsetLineColor;
        ctx.fillRect(x - boundaryWidth / 2, rowY, boundaryWidth, rowH);
      }
    }

    const drawCurve = (
      getMinute: (day: SuntimesLegacyDayRow) => number | null,
      color: string
    ): void => {
      const points: Array<{ x: number; y: number }> = [];

      for (let i = 0; i < this.yearData.length; i++) {
        const day = this.yearData[i];
        if (day.isAllDay || day.isAllNight) {
          continue;
        }

        const minute = getMinute(day);
        if (minute == null) {
          continue;
        }

        points.push({
          x: plotX + minute * minuteWidth,
          y: plotY + i * dayHeight + dayHeight / 2,
        });
      }

      if (points.length < 2) {
        return;
      }

      ctx.save();
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
      ctx.lineWidth = Math.max(3.5, boundaryWidth + 1.5);
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.stroke();

      ctx.strokeStyle = color;
      ctx.lineWidth = Math.max(2.5, boundaryWidth + 0.5);
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x, points[i].y);
      }
      ctx.stroke();
      ctx.restore();
    };

    drawCurve((day) => day.sunriseMin, this.sunriseLineColor);
    drawCurve((day) => day.sunsetMin, this.sunsetLineColor);
  }

  private drawGridAxes(
    ctx: CanvasRenderingContext2D,
    plotX: number,
    plotY: number,
    plotWidth: number,
    plotHeight: number,
    dayCount: number,
    dayHeight: number
  ): void {
    ctx.strokeStyle = '#dee2e6';
    ctx.fillStyle = '#6c757d';
    ctx.font = '11px sans-serif';
    ctx.lineWidth = 1;

    ctx.strokeRect(plotX, plotY, plotWidth, plotHeight);

    for (let hour = 0; hour <= 24; hour += 3) {
      const x = plotX + (hour / 24) * plotWidth;
      ctx.beginPath();
      ctx.moveTo(x, plotY);
      ctx.lineTo(x, plotY + plotHeight);
      ctx.stroke();

      const label = `${String(hour).padStart(2, '0')}:00`;
      ctx.fillText(label, x - 14, plotY + plotHeight + 18);
    }

    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';

    const monthLabels = [
      { month: 1, label: 'Ene' },
      { month: 2, label: 'Feb' },
      { month: 3, label: 'Mar' },
      { month: 4, label: 'Abr' },
      { month: 5, label: 'May' },
      { month: 6, label: 'Jun' },
      { month: 7, label: 'Jul' },
      { month: 8, label: 'Ago' },
      { month: 9, label: 'Sep' },
      { month: 10, label: 'Oct' },
      { month: 11, label: 'Nov' },
      { month: 12, label: 'Dic' },
    ];

    for (const { month, label } of monthLabels) {
      const index = this.yearData.findIndex((day) => day.date.getMonth() + 1 === month && day.date.getDate() === 1);
      if (index < 0) {
        continue;
      }

      const y = plotY + index * dayHeight;
      ctx.beginPath();
      ctx.moveTo(plotX, y);
      ctx.lineTo(plotX + plotWidth, y);
      ctx.strokeStyle = 'rgba(222, 226, 230, 0.8)';
      ctx.stroke();
      ctx.fillStyle = '#6c757d';
      ctx.fillText(label, plotX - 8, y + dayHeight / 2);
    }

    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  private isNightMinute(day: SuntimesLegacyDayRow, minute: number): boolean {
    if (day.isAllNight) {
      return true;
    }

    if (day.isAllDay) {
      return false;
    }

    if (day.sunriseMin == null || day.sunsetMin == null) {
      return true;
    }

    return minute < day.sunriseMin || minute >= day.sunsetMin;
  }

  private minutesToHHMM(totalMinutes: number): string {
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
}
